import { useEffect, useMemo, useState } from "react";
import { can, getStoredAuthUser } from "@/lib/authUser";
import {
  APP_NAV_MODULE_META,
  accessPermissionsNavItems,
  accountsNavItems,
  attendanceNavItems,
  catalogNavItems,
  companyNavItems,
  customerSupplierNavItems,
  dataBankNavItems,
  employeeOnlyNavItems,
  expenseNavItems,
  leaveNavItems,
  leaveRequestsNavItem,
  payrollNavItems,
  productNavItem,
  salesNavItem,
  saleReturnsNavItem,
  carExchangesNavItem,
  workPoliciesNavItem,
  navItemMatchesPath,
  type AppNavLeafItem,
  type AppNavModuleMeta,
} from "@/lib/appNavConfig";

export type AppNavModule = AppNavModuleMeta & { items: AppNavLeafItem[] };

export function firstLeaf(items: AppNavLeafItem[]): string | null {
  for (const item of items) {
    if (item.children && item.children.length > 0) {
      const nested = firstLeaf(item.children);
      if (nested) return nested;
    }
    if (item.url) return item.url;
  }
  return null;
}

/** First module whose any child matches `pathname` (stable module order). */
export function findActiveNavModule(pathname: string, modules: AppNavModule[]): AppNavModule | null {
  for (const m of modules) {
    if (m.items.some((i) => navItemMatchesPath(pathname, i))) return m;
  }
  return null;
}

export function useAppNavModules(): AppNavModule[] {
  const [authUser, setAuthUser] = useState(() => getStoredAuthUser());
  useEffect(() => {
    const onAuthUpdate = () => setAuthUser(getStoredAuthUser());
    window.addEventListener("auth_user_updated", onAuthUpdate);
    return () => window.removeEventListener("auth_user_updated", onAuthUpdate);
  }, []);

  return useMemo(() => {
    const u = authUser;

    const canViewEmployees = can(u, "employee_can_view_any");
    const canViewDepartments = can(u, "department_can_view_any");
    const canViewDesignations = can(u, "designation_can_view_any");
    const canViewLeaveYears = can(u, "leave_year_can_view_any");
    const canViewLeaveTypes = can(u, "leave_type_can_view_any");
    const canViewLeavePolicies = can(u, "leave_policy_can_view_any");
    const canViewLeaveRequests = can(u, "leave_request_can_view_any");
    const canViewWorkPolicies = can(u, "work_policy_can_view_any");
    const canViewSalaryPolicies = can(u, "salary_policy_can_view_any");
    const canViewWorkShifts = can(u, "work_shift_can_view_any");
    const canViewRosters = can(u, "roster_can_view_any");
    const canViewWorkingCalendar = can(u, "working_calendar_can_view_any");
    const canViewDailyAttendance = can(u, "attendance_daily_can_view_any");
    const canViewPunchLog = can(u, "attendance_punch_log_can_view_any");
    const canViewAttendanceAdjustments = can(u, "attendance_adjustment_can_view_any") || canViewDailyAttendance;
    const canViewAttendanceRules = can(u, "attendance_rule_can_view_any");
    const canViewExpenseCategories = can(u, "expense_category_can_view_any");
    const canViewExpenses = can(u, "expense_can_view_any");
    const canViewBanks = can(u, "bank_can_view_any");
    const canViewCurrencies = can(u, "currency_can_view_any");
    const canViewChartOfAccounts = can(u, "chart_of_account_can_view_any");
    const canViewEmployeeAllowanceTypes = can(u, "employee_allowance_type_can_view_any");
    const canViewEmployeeDeductionTypes = can(u, "employee_deduction_type_can_view_any");
    const canViewSalaryIncrements = can(u, "salary_increment_can_view_any");
    const canViewSalaryProcess = can(u, "salary_process_can_view_any");
    const canViewEmployeeLoans = can(u, "employee_loan_can_view_any");
    const canViewSalaryAdvances = can(u, "salary_advance_can_view_any");
    const canViewSalaryAdjustments = can(u, "salary_adjustment_can_view_any");
    const canViewProducts = can(u, "product_can_view_any");
    const canViewSales = can(u, "sale_can_view_any");
    const canViewSaleReturns = can(u, "sale_return_can_view_any");
    const canViewCustomers = can(u, "customer_can_view_any");
    const canViewSuppliers = can(u, "supplier_can_view_any");
    const canViewInvestors = can(u, "investor_can_view_any");
    const canViewPartyLedger =
      canViewCustomers || canViewSuppliers || canViewInvestors || can(u, "chart_of_account_can_view_any");
    const canViewCatalogCategories = can(u, "category_can_view_any");
    const canViewBrands = can(u, "brand_can_view_any");
    const canViewPurchaseCostCategories = can(u, "purchase_cost_category_can_view_any");
    const canViewPurchaseCostTypes = can(u, "purchase_cost_type_can_view_any");
    const canViewAttributeGroups = can(u, "attribute_group_can_view_any");
    const canViewCatalogAttributes = can(u, "catalog_attribute_can_view_any");
    const canViewWarehouses = can(u, "warehouse_can_view_any");
    const canViewCompanies = can(u, "company_can_view_any");
    const canViewBiometricDevices = can(u, "biometric_device_can_view_any");
    const canViewCompanyBiometricDevices = can(u, "company_biometric_device_can_view_any");
    const canViewPermissionCategories = can(u, "permission_category_can_view_any");
    const canViewManagedPermissions = can(u, "managed_permission_can_view_any");
    const canViewRoles = can(u, "role_can_view_any");

    const catalogFiltered = catalogNavItems.filter((i) => {
      if (i.url === "/categories") return canViewCatalogCategories;
      if (i.url === "/brands") return canViewBrands;
      if (i.url === "/vehicle-models") return canViewBrands;
      if (i.url === "/vehicle-versions") return canViewBrands;
      if (i.url === "/purchase-cost-categories") return canViewPurchaseCostCategories || canViewPurchaseCostTypes;
      if (i.url === "/purchase-cost-types") return canViewPurchaseCostTypes;
      if (i.url === "/attribute-groups") return canViewAttributeGroups;
      if (i.url === "/attributes") return canViewCatalogAttributes;
      if (i.url === "/ware-houses") return canViewWarehouses || canViewCatalogAttributes;
      return true;
    });

    const productsItems: AppNavLeafItem[] = [
      ...(canViewProducts ? [productNavItem] : []),
      ...catalogFiltered,
    ];

    const salesItems: AppNavLeafItem[] = [
      ...(canViewSales ? [salesNavItem] : []),
      ...(canViewSaleReturns ? [saleReturnsNavItem] : []),
    ];

    const customerSupplierFiltered = customerSupplierNavItems.filter((i) => {
      if (i.url === "/customers") return canViewCustomers;
      if (i.url === "/suppliers") return canViewSuppliers;
      if (i.url === "/investors") return canViewInvestors;
      if (i.url === "/parties/ledger") return canViewPartyLedger;
      return true;
    });

    const accountsFiltered = accountsNavItems.filter((i) => {
      if (i.url === "/accounts/company-ledger") return canViewChartOfAccounts || canViewBanks;
      if (i.url === "/accounts/trial-balance") return canViewChartOfAccounts || canViewBanks;
      if (i.url === "/accounts/income-statement") return canViewChartOfAccounts || canViewBanks;
      if (i.url === "/accounts/balance-sheet") return canViewChartOfAccounts || canViewBanks;
      if (i.url === "/accounts/chart-of-accounts") return canViewChartOfAccounts || canViewBanks;
      if (i.url === "/accounts/vouchers") return canViewChartOfAccounts || canViewBanks;
      return true;
    });

    const employeeFiltered = employeeOnlyNavItems.filter((i) => {
      if (i.url === "/employees") return canViewEmployees;
      if (i.url === "/employees/ledger") return canViewEmployees;
      if (i.url === "/departments") return canViewDepartments;
      if (i.url === "/designations") return canViewDesignations;
      return true;
    });
    const employeeItems: AppNavLeafItem[] = [
      ...employeeFiltered,
      ...(canViewWorkPolicies ? [workPoliciesNavItem] : []),
    ];

    const leaveFiltered = leaveNavItems.filter((i) => {
      if (i.url === "/leave-years") return canViewLeaveYears;
      if (i.url === "/leave-types") return canViewLeaveTypes;
      if (i.url === "/leave-policies") return canViewLeavePolicies;
      return true;
    });
    const leaveItemsBuilt: AppNavLeafItem[] = [...leaveFiltered, ...(canViewLeaveRequests ? [leaveRequestsNavItem] : [])];

    const payrollFiltered = payrollNavItems.filter((i) => {
      if (i.url === "/salary-policies") return canViewSalaryPolicies;
      if (i.url === "/employee-allowance-types") return canViewEmployeeAllowanceTypes;
      if (i.url === "/employee-deduction-types") return canViewEmployeeDeductionTypes;
      if (i.url === "/payroll/attendances") return canViewDailyAttendance || canViewSalaryProcess;
      if (i.url === "/payroll/salary-batches") return canViewSalaryProcess;
      if (i.url === "/payroll/salary-increments") return canViewSalaryIncrements;
      if (i.url === "/payroll/loans") return canViewEmployeeLoans;
      if (i.url === "/payroll/salary-advances") return canViewSalaryAdvances;
      if (i.url === "/payroll/salary-adjustments") return canViewSalaryAdjustments;
      return true;
    });

    const attendanceFiltered = attendanceNavItems.filter((i) => {
      if (i.url === "/work-shifts") return canViewWorkShifts;
      if (i.url === "/rosters") return canViewRosters;
      if (i.url === "/working-calendar") return canViewWorkingCalendar;
      if (i.url === "/attendance/daily") return canViewDailyAttendance;
      if (i.url === "/attendance/punches") return canViewPunchLog;
      if (i.url === "/attendance/adjustments") return canViewAttendanceAdjustments;
      if (i.url === "/attendance/rules") return canViewAttendanceRules;
      return true;
    });

    const expenseFiltered = expenseNavItems.filter((i) => {
      if (i.url === "/expense-categories") return canViewExpenseCategories;
      if (i.url === "/expenses") return canViewExpenses;
      return true;
    });

    const dataBankFiltered = dataBankNavItems.filter((i) => {
      if (i.url === "/banks") return canViewBanks;
      if (i.url === "/currencies") return canViewCurrencies;
      return true;
    });

    const companyFiltered = companyNavItems.filter((i) => {
      if (i.url === "/companies") return canViewCompanies;
      if (i.url === "/biometric-devices") return canViewBiometricDevices;
      if (i.url === "/company-biometric-devices") return canViewCompanyBiometricDevices;
      return true;
    });

    const accessFiltered = accessPermissionsNavItems.filter((i) => {
      if (i.url === "/permission-categories") return canViewPermissionCategories;
      if (i.url === "/permissions") return canViewManagedPermissions;
      if (i.url === "/roles") return canViewRoles;
      return true;
    });

    const itemsById: Record<string, AppNavLeafItem[]> = {
      "products-inventory": productsItems,
      sales: salesItems,
      "customer-supplier": customerSupplierFiltered,
      accounts: accountsFiltered,
      employee: employeeItems,
      leave: leaveItemsBuilt,
      payroll: payrollFiltered,
      attendance: attendanceFiltered,
      expense: expenseFiltered,
      "data-bank": dataBankFiltered,
      access: accessFiltered,
      company: companyFiltered,
    };

    return APP_NAV_MODULE_META.map((meta) => ({
      ...meta,
      items: itemsById[meta.id] ?? [],
    })).filter((m) => m.items.length > 0);
  }, [authUser]);
}
